import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { ScrollArea } from "../ui/scroll-area";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import {
  HardDrive,
  Search,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  XCircle,
  FileWarning,
  FolderX,
  FolderOpen,
  ArrowRightLeft,
  Trash2,
  Wrench,
} from "lucide-react";
import { toast } from "sonner";

const SEVERITY_CONFIG = {
  high: { color: "text-red-600", bg: "bg-red-50 dark:bg-red-950", border: "border-red-200 dark:border-red-800", badge: "destructive", icon: XCircle },
  medium: { color: "text-amber-600", bg: "bg-amber-50 dark:bg-amber-950", border: "border-amber-200 dark:border-amber-800", badge: "outline", icon: AlertTriangle },
  low: { color: "text-blue-600", bg: "bg-blue-50 dark:bg-blue-950", border: "border-blue-200 dark:border-blue-800", badge: "secondary", icon: FileWarning },
};

const TYPE_LABELS = {
  missing_file: "File mancante",
  wrong_client_folder: "Cartella cliente errata",
  wrong_subfolder: "Sottocartella errata",
  orphan_file: "File orfano",
  empty_folder: "Cartella vuota",
  orphan_folder: "Cartella senza cliente",
};

const TYPE_ICONS = {
  missing_file: XCircle,
  wrong_client_folder: ArrowRightLeft,
  wrong_subfolder: ArrowRightLeft,
  orphan_file: FileWarning,
  empty_folder: FolderX,
  orphan_folder: FolderOpen,
};

export function StorageAuditTab() {
  const [scanning, setScanning] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [anomalies, setAnomalies] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [fixResult, setFixResult] = useState(null);

  const handleScan = async () => {
    setScanning(true);
    setFixResult(null);
    setSelected(new Set());
    try {
      const res = await axios.get("/api/maintenance/storage/scan");
      setAnomalies(res.data.anomalies || []);
      setStats(res.data.stats);
      setScanned(true);
      if (!res.data.anomalies?.length) {
        toast.success("Nessuna anomalia trovata! Storage pulito.");
      } else {
        toast.info(`Trovate ${res.data.stats.anomalies_count} anomalie`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella scansione");
    } finally {
      setScanning(false);
    }
  };

  const toggleSelect = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === anomalies.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(anomalies.map((a) => getAnomalyKey(a))));
    }
  };

  const getAnomalyKey = (a) => a.document_id || a.file_path || a.folder_path;

  const handleFix = (fixAll) => {
    const count = fixAll ? anomalies.length : selected.size;
    if (count === 0) {
      toast.error("Seleziona almeno un'anomalia da correggere");
      return;
    }
    setConfirmText("");
    setConfirmDialog({ fixAll, count });
  };

  const confirmFix = async () => {
    const { fixAll } = confirmDialog;
    setConfirmDialog(null);
    setConfirmText("");
    setFixing(true);
    try {
      const payload = fixAll
        ? { fix_all: true }
        : { anomaly_ids: Array.from(selected) };
      const res = await axios.post("/api/maintenance/storage/fix", payload);
      setFixResult(res.data);
      const q = res.data?.quarantined || 0;
      toast.success(
        q > 0
          ? `Corrette ${res.data.fixed} anomalie — ${q} file spostati in quarantena (recuperabili)`
          : `Corrette ${res.data.fixed} anomalie`
      );
      // Re-scan after fix
      await handleScan();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella correzione");
    } finally {
      setFixing(false);
    }
  };

  const severityLabel = (s) => {
    if (s === "high") return "Alta";
    if (s === "medium") return "Media";
    return "Bassa";
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" data-testid="storage-audit-title">
            <HardDrive className="h-5 w-5" />
            Audit Storage Documenti
          </CardTitle>
          <CardDescription>
            Controlla che tutti i file siano nella posizione corretta (cartella cliente, sottocartella Polizze/Documenti/Targa).
            Rileva file orfani, riferimenti rotti e cartelle vuote.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3 flex-wrap">
            <Button onClick={handleScan} disabled={scanning} data-testid="scan-storage-btn">
              {scanning ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Search className="h-4 w-4 mr-2" />
              )}
              {scanning ? "Scansione..." : "Scansiona Storage"}
            </Button>

            {anomalies.length > 0 && (
              <>
                <Button
                  variant="outline"
                  onClick={() => handleFix(false)}
                  disabled={fixing || selected.size === 0}
                  data-testid="fix-selected-btn"
                >
                  <Wrench className="h-4 w-4 mr-2" />
                  Correggi Selezionati ({selected.size})
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleFix(true)}
                  disabled={fixing}
                  data-testid="fix-all-btn"
                >
                  {fixing ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Wrench className="h-4 w-4 mr-2" />
                  )}
                  Correggi Tutto ({anomalies.length})
                </Button>
              </>
            )}
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3 bg-muted rounded-lg text-center">
                <div className="text-2xl font-bold">{stats.total_files_disk}</div>
                <div className="text-xs text-muted-foreground">File su disco</div>
              </div>
              <div className="p-3 bg-muted rounded-lg text-center">
                <div className="text-2xl font-bold">{stats.total_docs_db}</div>
                <div className="text-xs text-muted-foreground">Documenti in DB</div>
              </div>
              <div className="p-3 bg-muted rounded-lg text-center">
                <div className={`text-2xl font-bold ${stats.anomalies_count > 0 ? "text-amber-600" : "text-green-600"}`}>
                  {stats.anomalies_count}
                </div>
                <div className="text-xs text-muted-foreground">Anomalie</div>
              </div>
              <div className="p-3 bg-muted rounded-lg text-center">
                <div className="text-2xl font-bold text-green-600">
                  {stats.total_files_disk - (stats.by_type?.orphan_file || 0)}
                </div>
                <div className="text-xs text-muted-foreground">File validi</div>
              </div>
            </div>
          )}

          {/* No anomalies */}
          {scanned && anomalies.length === 0 && (
            <div className="flex items-center gap-2 p-4 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-green-700 dark:text-green-300">
                Tutto in ordine! Nessuna anomalia rilevata nello storage.
              </span>
            </div>
          )}

          {/* Anomaly list */}
          {anomalies.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Anomalie trovate</span>
                <Button variant="ghost" size="sm" onClick={selectAll}>
                  {selected.size === anomalies.length ? "Deseleziona tutto" : "Seleziona tutto"}
                </Button>
              </div>
              <ScrollArea className="h-[420px] pr-3">
                <div className="space-y-2">
                  {anomalies.map((anomaly, i) => {
                    const key = getAnomalyKey(anomaly);
                    const sev = SEVERITY_CONFIG[anomaly.severity] || SEVERITY_CONFIG.low;
                    const Icon = TYPE_ICONS[anomaly.type] || FileWarning;
                    return (
                      <div
                        key={i}
                        className={`flex items-start gap-3 p-3 rounded-lg border ${sev.border} ${sev.bg} ${
                          selected.has(key) ? "ring-2 ring-blue-400" : ""
                        }`}
                      >
                        <Checkbox
                          checked={selected.has(key)}
                          onCheckedChange={() => toggleSelect(key)}
                          data-testid={`anomaly-check-${i}`}
                        />
                        <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${sev.color}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant={sev.badge}>{severityLabel(anomaly.severity)}</Badge>
                            <Badge variant="outline">{TYPE_LABELS[anomaly.type] || anomaly.type}</Badge>
                          </div>
                          <p className="text-sm mt-1">{anomaly.description}</p>
                          {anomaly.expected_subfolder && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Posizione corretta: <code className="bg-muted px-1 rounded">{anomaly.expected_client_folder || ""}/{anomaly.expected_subfolder}</code>
                            </p>
                          )}
                          {anomaly.relative_path && (
                            <p className="text-xs text-muted-foreground mt-0.5 font-mono truncate">
                              {anomaly.relative_path}
                            </p>
                          )}
                          {anomaly.size != null && (
                            <p className="text-xs text-muted-foreground">
                              {(anomaly.size / 1024).toFixed(1)} KB
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Fix results */}
          {fixResult && (
            <Card className="border-green-200 dark:border-green-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Risultato Correzione
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-1">
                <p>
                  <span className="font-medium text-green-600">{fixResult.fixed}</span> corrette,{" "}
                  <span className="text-muted-foreground">{fixResult.skipped}</span> saltate
                </p>
                {fixResult.details?.map((d, i) => (
                  <p key={i} className="text-xs text-muted-foreground">• {d}</p>
                ))}
                {fixResult.errors?.map((e, i) => (
                  <p key={i} className="text-xs text-red-500">• {e}</p>
                ))}
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={() => setConfirmDialog(null)}>
        <DialogContent aria-describedby="fix-confirm-desc">
          <DialogHeader>
            <DialogTitle>Conferma Correzione Storage</DialogTitle>
            <DialogDescription id="fix-confirm-desc">
              Le seguenti azioni verranno eseguite automaticamente.
            </DialogDescription>
          </DialogHeader>
          {confirmDialog && (
            <div className="space-y-3 py-2">
              <p className="text-sm">
                Verranno corrette <span className="font-bold">{confirmDialog.count}</span> anomalie.
              </p>
              <div className="p-3 bg-amber-50 dark:bg-amber-950 rounded-lg text-sm">
                <p className="font-medium mb-1">Azioni previste:</p>
                <ul className="list-disc list-inside text-xs space-y-0.5">
                  <li>File in posizione errata → spostati nella cartella corretta</li>
                  <li>Riferimenti a file mancanti → <span className="font-semibold">segnalati</span> (il riferimento NON viene cancellato)</li>
                  <li>File orfani (non nel DB) → <span className="font-semibold">spostati in quarantena</span>, non eliminati</li>
                  <li>Cartelle vuote → eliminate</li>
                  <li>Cartelle orfane vuote → eliminate</li>
                  <li>Cartelle orfane con file → richiedono revisione manuale</li>
                </ul>
                <p className="text-xs mt-2">
                  I file spostati finiscono in <code className="bg-muted px-1 rounded">_quarantena/</code> e restano recuperabili.
                </p>
              </div>
              {confirmDialog.fixAll && (
                <div className="space-y-1">
                  <Label htmlFor="storage-confirm-text" className="text-xs">
                    Stai correggendo TUTTO: per confermare scrivi <span className="font-mono font-bold">CORREGGI</span>
                  </Label>
                  <Input
                    id="storage-confirm-text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="CORREGGI"
                    className="h-8"
                    data-testid="storage-confirm-input"
                  />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmDialog(null); setConfirmText(""); }}>Annulla</Button>
            <Button
              variant="destructive"
              onClick={confirmFix}
              disabled={fixing || (confirmDialog?.fixAll && confirmText.trim().toUpperCase() !== "CORREGGI")}
              data-testid="confirm-fix-btn"
            >
              {fixing ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Wrench className="h-4 w-4 mr-2" />
              )}
              Procedi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
